// 布局对象公共部分
import {useMemo, useRef, useState} from "react";
import {useRequest, useUpdateEffect} from "ahooks";
import {useIntl} from "umi";
import {intersection, isEmpty} from "lodash-es";
import { DashboardDefaultTabId, getDashboard } from '@dbl/services/dashboard';
import { getChartList } from '@dbl/services/chart';
import {LANGUAGE_EN} from "@home/_config/config";
import {batchedUpdates} from "@home/utils/utils";
import {getOptionsOfVirtual, queryParams} from "@/pages/datalab/components/DashboardLayout/_utils";
import {
  EMPTY_USER_FILTER,
  type ITabLayout,
  type TLayoutChartType,
  type TLayoutFilterType,
} from '@dbl/core/dashboard';
import { allowChartTypeShowUserFilter } from '@dbl/core/global';

export type {
  TLayoutFilterType,
  TLayoutChartType,
  TLayoutTextType,
  TLayoutAiAnalysisType,
} from '@dbl/core/dashboard';

export const useDashboardReportStore = (props) => {
  // gameCode
  const {system, dashboardId, attributes, defaultTabId, defaultChartInfoLoading} = props;
  
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  
  // 报表信息数组
  const [tabLayouts, setTabLayouts] = useState<ITabLayout[]>([]);
  
  // 当前报表头部的tabId
  const [currentTabId, setCurrentTabId] = useState<string>('');
  
  const [showContact, setShowContact] = useState<boolean | undefined>(undefined); // 是否显示报表联系人 (创建报表的时候需要是undefined)
  const [contactInfo, setContactInfo] = useState({}); // 报表联系人信息
  
  // 记录报表初始化的数据， 用于判断用户是否改变了报表，保存按钮是否禁用。TODO 属于报表编辑的逻辑，后期优化
  const originalTabLayoutsJson = useRef('[]'); // JSON字符串
  
  // 记录已经加载过图表的页签id
  const loadedChartsTabIds = useRef<string[]>([]);
  
  // 先发起报表info请求
  const {data: dashboardInfo, loading, error, runAsync} = useRequest(getDashboard, {
    manual: true,
    onSuccess: (data) => {
      batchedUpdates(() => { // 批量更新
        const tabs = data?.tabs || [];
        originalTabLayoutsJson.current = JSON.stringify(tabs);
        setContactInfo(data?.contact || {});
        setShowContact(data?.show_contact);
        setTabLayouts(tabs);
        setCurrentTabId(defaultTabId || tabs[0]?.tabId || '');
      });
    },
  });
  
  const [chartsInfoLoading, setChartsInfoLoading] = useState(defaultChartInfoLoading ?? true);
  
  // 根据当前layouts的图表id 请求图表信息
  const {error: chartsInfoError, run: chartsInfoRun} = useRequest(getChartList, {
    manual: true,
    onSuccess(data) {
      batchedUpdates(() => {
        // 图表新数据加载成功后，保存到数组中。
        loadedChartsTabIds.current.push(currentTabId);
        // 更新 tabLayouts
        const chartInfoMap = data?.data_list?.reduce((acc, chart) => {
          acc[chart.task_id] = chart;
          return acc;
        }, {});
        // 后端返回的data.data_list 是null 或空数组时, 无需重新设置
        !isEmpty(chartInfoMap) && setTabLayouts((tabLayouts) => {
    
          // 判断 用户是否已经更改过报表了。 为true代表没有更改过 （TODO 属于编辑的逻辑）
          const flag = originalTabLayoutsJson.current === JSON.stringify(tabLayouts);
    
          tabLayouts?.forEach((tabLayout) => {
            if (tabLayout.tabId === currentTabId) {
              // 整体使用引用地址更新数据
        
              // 找到筛选器中对应的scope图表进行数据更新
              tabLayout.filters = tabLayout.filters?.map((filter) => {
                if (!filter?.scope?.scope_charts) return filter;
                return {
                  ...filter,
                  scope: {
                    ...filter?.scope,
                    scope_charts: filter?.scope?.scope_charts?.map(chart => {
                      const chartInfo = chartInfoMap[chart?.task_id] ?? chart;
                      return {
                        ...chart,
                        name: chartInfo.name,
                        nameCn: chartInfo.name_cn,
                      };
                    }),
                  },
                };
              });

              tabLayout.userFilter = tabLayout.userFilter ? {
                ...tabLayout.userFilter,
                controls: tabLayout.userFilter.controls?.map((filter) => {
                  if (!filter?.scope?.scope_charts) return filter;
                  return {
                    ...filter,
                    scope: {
                      ...filter?.scope,
                      scope_charts: filter?.scope?.scope_charts.map(chart => {
                        const chartInfo = chartInfoMap[chart?.task_id] ?? chart;
                        return {
                          ...chart,
                          name: chartInfo.name,
                          nameCn: chartInfo.name_cn,
                        };
                      }),
                    },
                  };
                }),
              } : tabLayout.userFilter;

              // 兼容旧数据scope_charts设置字段：(一期保留的scope_charts包含所有图表类型，后续优化scope_charts只包含allowChartTypeShowUserFilter类型)
              if (Array.isArray(tabLayout?.userFilter?.scope_charts)) {
                tabLayout.userFilter.scope_charts = tabLayout?.userFilter?.scope_charts?.filter(chart => {
                  const chartInfo = chartInfoMap[chart?.task_id] ?? chart;
                  chart.name = chartInfo?.name;
                  chart.nameCn = chartInfo?.name_cn;
                  // 表格 aggregate聚合模式 raw_data明细模式
                  const mode = JSON.parse(chartInfo?.filter || '{}')?.query_mode;
                  return allowChartTypeShowUserFilter({chartType: chart?.chart_type, mode});
                });
              }

              // 更新布局图表中的图表数据
              const newLayouts = [];
              tabLayout?.layouts?.forEach(layout => {
                const chart = chartInfoMap[layout?.task_id];
                // 表格 aggregate聚合模式 raw_data明细模式
                const mode = JSON.parse(chart?.filter || '{}')?.query_mode;
                let newLayout = layout;
                if (chart) {
                  newLayout = {
                    ...layout,
                    ...chart,
                    mode,
                    chartType: chart.chart_type,
                    // 【Datalab】【优化】【需测】报表编辑态交互优化：网格：由6列调整至12列
                    // 删除所有的宽高限制，避免图表卡片无法改变宽高
                    minH: undefined,
                    minW: undefined,
                    maxH: undefined,
                    maxW: undefined,
                  };
                }
                // 过滤掉保存信息中 布局参数为null或undefined的情况, 避免第三方库死循环.
                if (newLayout.x == null || newLayout.y == null || newLayout.w == null || newLayout.h == null) return;
                
                newLayouts.push(newLayout);
              });
              tabLayout.layouts = newLayouts;
            }
          });
    
          // 在用户没有更改过报表的情况下，切换到一个新页签时，更新了图表信息，那么要更改报表的原始信息。（TODO 属于编辑的逻辑）
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          flag && (originalTabLayoutsJson.current = JSON.stringify(tabLayouts));
          return [...tabLayouts];
        });
        setChartsInfoLoading(false);
        
      });

    },
    onError() {
      setChartsInfoLoading(false);
    }
  });
  
  
  // 头部 页签
  const headerTabs = useMemo(() => {
    // if (!currentTabId || currentTabId === DashboardDefaultTabId) return [];
    // TODO ChartInfo请求成功更新tabLayouts, 导致更新headerTabs (实际不必要)
    const tabs: {id: string; label: string;}[] = [];
    tabLayouts?.forEach((item) => {
      (item.tabId !== DashboardDefaultTabId) && tabs.push({
        id: item.tabId,
        label: isEn ? item.tabName : (item.tabNameCN ?? item.tabName),
      });
    });
    return tabs;
  }, [tabLayouts, isEn]);
  // 图表信息请求失败, 处理逻辑?
  
  const {
    filters = [],
    layouts = [],
    userFilter = EMPTY_USER_FILTER,
    enableCollapseFilter = false,
  } = useMemo(() => {
    const dataSource = tabLayouts?.find(item => item.tabId === currentTabId) ?? {};
    attributes && dataSource?.filters?.forEach((element) => {
      const urlOptionStr = queryParams(attributes)?.[element.i ?? element.filterKey];
      if (urlOptionStr) { // 管理端配置了筛选器
        const initialValue = getOptionsOfVirtual(element, urlOptionStr);
        if (!isEmpty(initialValue)) {
          // 初始化直接赋值
          if (element.filterType === 'string') {
            // 取交集
            if (element.isFull || isEmpty(element.defaultValue)) {
              element.value = initialValue;
            } else {
              const result = intersection(initialValue, element.defaultValue);
              if (result?.length > 0) {
                element.value = result;
                element.isFull = result?.length === initialValue?.length;
              } else {
                // 如果 交集结果为空, 则默认全选
                element.value = initialValue;
                element.isFull = true;
              }
            }
          } else {
            element.value = initialValue;
          }
          element.defaultValue = element.value; // 因为 attributes 是虚拟版本配置的筛选器选项, 只在承接页面使用, 无法进行报表的修改, 所以设置defaultValue 不会改变原有报表的默认值
          element.scope_value = initialValue; //  虚拟配置的选项, 上传给后端
          
        }
      }
      
    });
    return dataSource;
  }, [tabLayouts, attributes, currentTabId]);

  // 获取当前tab下userFilter的应用图表范围
  const userFilterScopeCharts = useMemo(() => (layouts?.reduce((acc: Partial<TLayoutChartType>[], item: TLayoutChartType) => {
    if (item.type !== 'text' && allowChartTypeShowUserFilter({chartType: item.chart_type, mode: item.mode})) {
      acc.push({
        task_id: item?.task_id,
        name: item?.name,
        nameCn: item?.name_cn,
        chart_type: item?.chart_type,
        table_id: item?.table_id,
      });
    }
    return acc;
  }, [])),[layouts]);
  
  useUpdateEffect(() => {
    // 如果已经加载过最新图表数据的页签，将不再加载
    if (loadedChartsTabIds.current?.includes(currentTabId)) {
      setChartsInfoLoading(false);
      return;
    }
    // dashboardInfo 没有值时, 代表是创建报表, 不需要请求图表信息
    if (!dashboardInfo) {
      setChartsInfoLoading(false);
      return;
    }
    
    // 如果没有加载过最新图表数据的页签，发起请求
    if (!loadedChartsTabIds.current?.includes(currentTabId)) {
      // 获取 图表id
      const chartIds: string[] = [];
      layouts?.forEach((item) => {
        if (item.task_id) {
          chartIds.push(String(item.task_id));
        }
        
        // TODO 页签卡片的图表id 还需要一层循序
        
      });
      if (chartIds.length > 0) {
        setChartsInfoLoading(true);
        // 发起图表信息请求
        chartsInfoRun( {
          game_code: dashboardInfo?.game_code, // TODO 其他系统没有gameCode? 使用报表返回的gameCode
          sub_labels: [],
          all_selected: true,
          keywords: '',
          order_by: 'create_time',
          desc: true,
          page: 1,
          page_size: 1000000000,
          system,
          task_ids: chartIds
        });
      } else {
        setChartsInfoLoading(false);
      }
      
    }
    
  }, [currentTabId]);
  
  // 其他系统的承接页面都是使用一个业务组件，在承接页面之间切换时，业务组件不会被销毁，需要重置状态。
  useUpdateEffect(() => {
    setTabLayouts([]);
    setCurrentTabId('');
    originalTabLayoutsJson.current = '[]';
    loadedChartsTabIds.current = [];
  }, [dashboardId]);

  return {
    // 报表信息相关
    dashboardInfo,
    dashboardInfoLoading: loading,
    dashboardInfoError: error,
    dashboardInfoRunAsync: runAsync,
    // 图表请求相关
    chartsInfoLoading,
    setChartsInfoLoading,
    chartsInfoError,
    // 布局相关
    tabLayouts,
    setTabLayouts,
    // 当前页签
    currentTabId,
    setCurrentTabId,
    // 是否显示联系人
    showContact,
    setShowContact,
    // 联系人信息
    contactInfo,
    setContactInfo,
    headerTabs,
    filters,
    layouts,
    userFilter,
    enableCollapseFilter,
    // pageLoading,
    originalTabLayoutsJson,
    loadedChartsTabIds,
    userFilterScopeCharts,
  };
  
};